import { useEffect, useState, useCallback } from 'react';
import { confirmDialog, alertDialog } from '../telegram';
import {
  getClientMe,
  upsertCartItem,
  clearBackendCart,
  createOrder,
  checkTodayOrder,
  getPublicSettings,
  getDepositMe,
} from '../api';
import { useCart } from '../hooks/useCart';
import CatalogPage from './CatalogPage';
import CartPage from './CartPage';
import ClientStatusPage from './ClientStatusPage';
import CoffeeShopSelectPage from './CoffeeShopSelectPage';
import OrderSuccessPage from './OrderSuccessPage';
import OrderHistoryPage from './OrderHistoryPage';
import DepositPage from './DepositPage';
import NotificationsPage from './NotificationsPage';
import TopUpModal from '../components/TopUpModal';
import type { CoffeeShop, OrderCreated, ClientInfo, DepositAccount } from '../types';

// ============================================================================
// Этап W2 (веб-заказы): полноценный магазин для обычного клиента (роль client)
// в браузере вне Telegram. Повторяет клиентскую часть оркестрации из App.tsx —
// каталог → корзина → выбор точки → оформление → успех/история — но навигация
// идёт веб-кнопками (см. components/WebNav), т.к. нативных кнопок Telegram в
// браузере нет. Подключается из WebAuth (кабинет) для подтверждённого клиента.
// ============================================================================

type Page = 'catalog' | 'cart' | 'client-status' | 'shop-select' | 'order-success' | 'order-history' | 'deposit' | 'notifications';

export default function WebShop({ onLogout }: { onLogout: () => void }) {
  const [page, setPage] = useState<Page>('catalog');
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  // Минимальная сумма заказа (руб.). 0 — ограничение выключено / ещё не загружено.
  const [minOrderAmount, setMinOrderAmount] = useState(0);
  const [coffeeShops, setCoffeeShops] = useState<CoffeeShop[]>([]);
  const [selectedClientInfo, setSelectedClientInfo] = useState<ClientInfo | null>(null);
  const [createdOrder, setCreatedOrder] = useState<OrderCreated | null>(null);

  const { items, add, dec, getQty, totalItems, totalPrice, clear } = useCart();

  const goCart = useCallback(() => setPage('cart'), []);
  const goCatalog = useCallback(() => setPage('catalog'), []);
  const goHistory = useCallback(() => setPage('order-history'), []);
  const goDeposit = useCallback(() => setPage('deposit'), []);
  // Блок «Уведомления в приложении»: экран уведомлений и переход к объекту из него.
  const goNotifications = useCallback(() => setPage('notifications'), []);
  const openNotificationEntity = useCallback((entityType: string | null) => {
    if (entityType === 'order') setPage('order-history');
    else if (entityType === 'deposit') setPage('deposit');
    else setPage('catalog');
  }, []);

  useEffect(() => {
    getPublicSettings()
      .then(({ data }) => setMinOrderAmount(Number(data.min_order_amount) || 0))
      .catch(() => { /* не критично — остаётся 0 (без лимита) */ });
  }, []);

  // Блок B: депозиты моих кофеен и окно пополнения (как в App.tsx).
  const [deposits, setDeposits] = useState<DepositAccount[]>([]);
  const [depositLoading, setDepositLoading] = useState(false);
  const [depositsLoaded, setDepositsLoaded] = useState(false);
  const [topUpOpen, setTopUpOpen] = useState(false);
  // Блок D: точка, для которой открыто окно пополнения со страницы «Депозит».
  const [topUpShopId, setTopUpShopId] = useState<string | null>(null);
  const loadDeposits = useCallback(async () => {
    setDepositLoading(true);
    try {
      const { data } = await getDepositMe();
      setDeposits(data);
    } catch {
      /* не критично — гейт проверит бэкенд при оформлении */
    } finally {
      setDepositLoading(false);
      setDepositsLoaded(true);
    }
  }, []);
  // Блок D: остаток нужен уже в шапке каталога, на странице «Депозит» и в корзине.
  useEffect(() => {
    if (page === 'catalog' || page === 'cart' || page === 'deposit') { loadDeposits(); }
  }, [page, loadDeposits]);
  const openTopUp = useCallback(() => {
    if (deposits.length === 0) {
      alertDialog('Не найдена кофейня для пополнения депозита. Обратитесь к менеджеру.');
      return;
    }
    setTopUpOpen(true);
  }, [deposits.length]);
  const openTopUpFor = useCallback((shopId: string) => { setTopUpShopId(shopId); setTopUpOpen(true); }, []);
  const closeTopUp = useCallback(() => { setTopUpOpen(false); setTopUpShopId(null); loadDeposits(); }, [loadDeposits]);
  const activeDeposit: DepositAccount | null = deposits.length === 1 ? deposits[0] : null;
  const topUpModal = topUpOpen && deposits.length > 0
    ? <TopUpModal shops={deposits} initialShopId={topUpShopId ?? activeDeposit?.coffee_shop_id ?? null} onClose={closeTopUp} />
    : null;

  const placeOrder = useCallback(async (shop: CoffeeShop) => {
    setCheckoutLoading(true);
    try {
      // Мягкое предупреждение о повторном заказе на ту же точку в текущий
      // операционный день (как в Mini App). Не блокируем — спрашиваем подтверждение.
      const { data: chk } = await checkTodayOrder(shop.id);
      if (chk.has_order_today) {
        const ok = await confirmDialog(
          `Сегодня на «${shop.name}» уже оформлен заказ ${chk.public_number ?? ''}. Создать ещё один?`,
        );
        if (!ok) { setPage('cart'); return; }
      }
      await clearBackendCart(shop.id);
      for (const item of items) {
        await upsertCartItem(shop.id, item.product.id, item.quantity);
      }
      const { data } = await createOrder(shop.id);
      clear();
      setCreatedOrder(data);
      setPage('order-success');
    } catch (e) {
      alert(`Ошибка оформления: ${(e as Error).message}`);
      setPage('cart');
      loadDeposits();
    } finally {
      setCheckoutLoading(false);
    }
  }, [items, clear, loadDeposits]);

  const goCheckout = useCallback(async () => {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    try {
      const { data: clientData } = await getClientMe();
      setSelectedClientInfo(clientData.client);
      if (clientData.client.status !== 'approved') {
        setPage('client-status');
        return;
      }
      const activeShops = clientData.coffee_shops.filter(s => s.status === 'active');
      if (activeShops.length === 0) {
        setPage('client-status');
        return;
      }
      if (activeShops.length === 1) {
        await placeOrder(activeShops[0]);
      } else {
        setCoffeeShops(activeShops);
        setPage('shop-select');
      }
    } catch (e) {
      alert(`Ошибка: ${(e as Error).message}`);
    } finally {
      setCheckoutLoading(false);
    }
  }, [checkoutLoading, placeOrder]);

  if (page === 'deposit') {
    return (
      <>
        <DepositPage onBack={goCatalog} backLabel="‹ В каталог" deposits={deposits} loading={depositLoading} loaded={depositsLoaded} onTopUp={openTopUpFor} />
        {topUpModal}
      </>
    );
  }
  if (page === 'notifications') return <NotificationsPage onBack={goCatalog} backLabel="‹ В каталог" onOpenEntity={openNotificationEntity} />;
  if (page === 'order-history') return <OrderHistoryPage onBack={goCatalog} />;
  if (page === 'order-success' && createdOrder) {
    return <OrderSuccessPage order={createdOrder} onHistory={goHistory} onContinue={goCatalog} />;
  }
  if (page === 'shop-select') {
    return <CoffeeShopSelectPage shops={coffeeShops} onSelect={placeOrder} onBack={goCart} loading={checkoutLoading} />;
  }
  if (page === 'client-status') return <ClientStatusPage info={selectedClientInfo} onBack={goCatalog} />;
  if (page === 'cart') {
    return (
      <>
        <CartPage
          onBack={goCatalog}
          onCheckout={goCheckout}
          checkoutLoading={checkoutLoading}
          items={items}
          add={add}
          dec={dec}
          totalItems={totalItems}
          totalPrice={totalPrice}
          minOrderAmount={minOrderAmount}
          deposit={activeDeposit}
          depositLoading={depositLoading}
          onTopUp={openTopUp}
          onDeposit={goDeposit}
        />
        {topUpModal}
      </>
    );
  }
  return (
    <CatalogPage
      onCartOpen={goCart}
      onHistory={goHistory}
      onDeposit={goDeposit}
      depositAvailable={activeDeposit?.available ?? null}
      add={add}
      dec={dec}
      getQty={getQty}
      totalItems={totalItems}
      totalPrice={totalPrice}
      onExit={onLogout}
      onNotifications={goNotifications}
    />
  );
}
